<template>
  <div class="signature-capture-wrapper">
    <div class="signature-header" v-if="title">
      <h4>{{ title }}</h4>
    </div>

    <p v-if="showEditPrompt && isEditing" class="signature-prompt">
      Please sign below
    </p>

    <div class="signatureWrapper">
      <!-- Signature Pad (Edit Mode) -->
      <div v-if="isEditing" class="signature-canvas-container">
        <vueSignature
          :ref="signatureRef"
          :sigOption="mergedSignatureOptions"
          :w="canvasWidth"
          :h="canvasHeight"
          :disabled="disabled"
        />
      </div>

      <!-- Signature Display (View Mode) -->
      <img
        v-else-if="signatureImage"
        :src="getSignatureImageUrl(signatureImage)"
        :alt="title || 'Signature'"
        class="signature-display"
      />

      <!-- No Signature Placeholder -->
      <div v-else class="no-signature-placeholder">
        <i class="bi bi-pen" aria-hidden="true"></i>
        <span>No signature captured</span>
      </div>

      <!-- Action Buttons -->
      <div class="signature-actions player-game-responce">
        <template v-if="isEditing">
          <button
            type="button"
            @click="handleSave"
            class="btn-save"
            :disabled="saving"
          >
            <span v-if="saving">Saving...</span>
            <span v-else>Save</span>
          </button>
          <button type="button" @click="handleClear" class="btn-clear">
            Clear
          </button>
          <button
            v-if="signatureImage && allowCancel"
            type="button"
            @click="handleCancel"
            class="btn-cancel"
          >
            Cancel
          </button>
        </template>
        <template v-else-if="editable && signatureImage">
          <button type="button" @click="handleEdit" class="btn-edit">
            Edit
          </button>
        </template>
        <template v-else-if="editable && !signatureImage">
          <button type="button" @click="handleEdit" class="btn-add">
            Add Signature
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import vueSignature from "vue-signature";

export default {
  name: "SignatureCapture",
  components: {
    vueSignature,
  },
  props: {
    // Display title for the signature section
    title: {
      type: String,
      default: "",
    },
    // Unique identifier for the signature ref
    signatureId: {
      type: String,
      required: true,
    },
    // Current signature image URL/path
    signatureImage: {
      type: String,
      default: null,
    },
    // Whether the signature is in edit mode
    modelValue: {
      type: Boolean,
      default: false,
    },
    // Whether the signature can be edited
    editable: {
      type: Boolean,
      default: true,
    },
    // Whether the signature pad is disabled
    disabled: {
      type: Boolean,
      default: false,
    },
    // Canvas width (use '100%' for responsive)
    width: {
      type: String,
      default: "100%",
    },
    // Canvas height
    height: {
      type: String,
      default: "200px",
    },
    // Signature pad options
    signatureOptions: {
      type: Object,
      default: () => ({}),
    },
    // Show "Please sign below" prompt
    showEditPrompt: {
      type: Boolean,
      default: true,
    },
    // Allow cancel button when editing existing signature
    allowCancel: {
      type: Boolean,
      default: true,
    },
    // Base path for images
    imagePath: {
      type: String,
      default: "",
    },
    // Saving state (controlled externally)
    saving: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:modelValue", "save", "clear", "edit", "cancel"],
  data() {
    return {
      defaultSignatureOptions: {
        penColor: "rgb(0, 0, 0)",
        backgroundColor: "rgb(255, 255, 255)",
      },
    };
  },
  computed: {
    isEditing: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },
    signatureRef() {
      return `signature_${this.signatureId}`;
    },
    mergedSignatureOptions() {
      return {
        ...this.defaultSignatureOptions,
        ...this.signatureOptions,
      };
    },
    canvasWidth() {
      // If width is 100%, we'll handle it via CSS
      return this.width;
    },
    canvasHeight() {
      return this.height;
    },
  },
  methods: {
    getSignatureImageUrl(imagePath) {
      if (!imagePath) return "";

      // If it's already a full URL or data URI, return as-is
      if (imagePath.startsWith("http") || imagePath.startsWith("data:")) {
        return imagePath;
      }

      // If a custom image path is provided, use it
      if (this.imagePath) {
        return `${this.imagePath}${imagePath}`;
      }

      // Default image path
      return `images/${imagePath}`;
    },

    getSignatureData(format = "image/jpeg") {
      const signatureRef = this.$refs[this.signatureRef];
      if (!signatureRef) {
        console.error(`Signature ref "${this.signatureRef}" not found`);
        return null;
      }
      return signatureRef.save(format);
    },

    isEmpty() {
      const signatureRef = this.$refs[this.signatureRef];
      if (!signatureRef) return true;
      return signatureRef.isEmpty();
    },

    handleSave() {
      const signatureData = this.getSignatureData();
      if (signatureData) {
        this.$emit("save", {
          signatureId: this.signatureId,
          signatureData,
          isEmpty: this.isEmpty(),
        });
      }
    },

    handleClear() {
      const signatureRef = this.$refs[this.signatureRef];
      if (signatureRef) {
        signatureRef.clear();
        this.$emit("clear", { signatureId: this.signatureId });
      }
    },

    handleEdit() {
      this.isEditing = true;
      this.$emit("edit", { signatureId: this.signatureId });
    },

    handleCancel() {
      this.isEditing = false;
      this.$emit("cancel", { signatureId: this.signatureId });
    },

    // Public method to programmatically get signature data
    getSignature(format = "image/jpeg") {
      return this.getSignatureData(format);
    },

    // Public method to clear the signature
    clear() {
      this.handleClear();
    },
  },
};
</script>

<style scoped>
.signature-capture-wrapper {
  margin-bottom: 1.5rem;
}

.signature-header h4 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.signature-prompt {
  color: #666;
  font-style: italic;
  margin-bottom: 0.5rem;
}

.signatureWrapper {
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 1rem;
  background-color: #fafafa;
  overflow: hidden;
}

.signature-canvas-container {
  width: 100%;
  overflow: hidden;
}

.signature-canvas-container canvas {
  max-width: 100% !important;
  height: auto !important;
  min-height: 150px;
  touch-action: none;
}

.signature-display {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
}

.no-signature-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #999;
  background-color: #f5f5f5;
  border-radius: 4px;
  min-height: 150px;
}

.no-signature-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.signature-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.signature-actions button {
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-save {
  background-color: #28a745;
  color: white;
  border: 1px solid #28a745;
}

.btn-save:hover:not(:disabled) {
  background-color: #218838;
}

.btn-save:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-clear {
  background-color: #6c757d;
  color: white;
  border: 1px solid #6c757d;
}

.btn-clear:hover {
  background-color: #5a6268;
}

.btn-cancel {
  background-color: #dc3545;
  color: white;
  border: 1px solid #dc3545;
}

.btn-cancel:hover {
  background-color: #c82333;
}

.btn-edit,
.btn-add {
  background-color: #007bff;
  color: white;
  border: 1px solid #007bff;
}

.btn-edit:hover,
.btn-add:hover {
  background-color: #0069d9;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .signature-capture-wrapper {
    margin-bottom: 1rem;
  }

  .signature-header h4 {
    font-size: 1rem;
  }

  .signatureWrapper {
    padding: 0.5rem;
  }

  .signature-canvas-container {
    margin: 0 -0.25rem;
  }

  .signature-canvas-container canvas {
    min-height: 120px;
  }

  .no-signature-placeholder {
    padding: 1.5rem;
    min-height: 100px;
  }

  .no-signature-placeholder i {
    font-size: 1.5rem;
  }

  .no-signature-placeholder span {
    font-size: 0.85rem;
  }

  .signature-actions {
    justify-content: center;
  }

  .signature-actions button {
    flex: 1;
    min-width: 70px;
    padding: 0.4rem 0.75rem;
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .signatureWrapper {
    padding: 0.4rem;
    border-radius: 4px;
  }

  .signature-canvas-container canvas {
    min-height: 100px;
  }

  .signature-actions button {
    padding: 0.35rem 0.5rem;
    font-size: 0.8rem;
  }
}
</style>
